import React, { useEffect, useState } from "react";
import { Bot, Check, FilePenLine, Loader2, Search, Sparkles } from "lucide-react";
import { useTranslation } from "../../../hooks/useTranslation";

// Checklist di default del loader (frame Figma 4044:25370/25507, stile
// "Modern Loading State for Generative AI"): usata per la fase di
// creazione finale del bot (ramo custom). Le fasi di ANALISI (prompt/sito)
// passano invece un elenco `steps` diverso, coerente con gli eventi reali
// emessi dal backend (vedi CustomBotWizard).
const STEPS = [
  { key: "loaderStep1", icon: Search },
  { key: "loaderStep2", icon: Sparkles },
  { key: "loaderStep3", icon: FilePenLine },
  { key: "loaderStep4", icon: Bot },
];

const STEP_MS = 1400; // avanzamento "narrativo" tra i task
const FAST_MS = 350; // recupero rapido se l'API ha già finito
const EXIT_MS = 800; // pausa a checklist completa prima della success

/**
 * Schermata di caricamento della creazione bot: mesh gradient animato a
 * tutto schermo + card con i 4 task sequenziali.
 *
 * Due modalità:
 * - `progress` (0..4) fornito → step VERI: ogni spunta arriva dagli eventi
 *   reali del backend (crawl/identity/ai) + creazione bot; la card insegue
 *   il progresso con un breve passo visivo per restare leggibile.
 * - senza `progress` → coreografia a timer come prima (`done` fa da
 *   cancello reale sull'ultimo step).
 */
const CreationLoader = ({ done, onFinished, progress = null, steps = STEPS }) => {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const last = steps.length - 1;
  const realMode = progress !== null && progress !== undefined;

  // Modalità reale: insegui `progress` un passo alla volta (FAST_MS) così
  // anche un burst di eventi resta una sequenza visiva leggibile.
  useEffect(() => {
    if (!realMode) return undefined;
    const target = Math.max(0, Math.min(progress, steps.length));
    if (current < target) {
      const timer = setTimeout(() => setCurrent((c) => c + 1), FAST_MS);
      return () => clearTimeout(timer);
    }
    if (current >= steps.length) {
      const timer = setTimeout(onFinished, EXIT_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [realMode, progress, current, onFinished, steps.length]);

  // Modalità coreografica (ramo custom): avanzamento a timer, done = gate.
  useEffect(() => {
    if (realMode) return undefined;
    if (current < last) {
      const timer = setTimeout(
        () => setCurrent((c) => c + 1),
        done ? FAST_MS : STEP_MS
      );
      return () => clearTimeout(timer);
    }
    if (current === last && done) {
      const timer = setTimeout(() => setCurrent(last + 1), 600);
      return () => clearTimeout(timer);
    }
    if (current > last) {
      const timer = setTimeout(onFinished, EXIT_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [realMode, current, done, last, onFinished]);

  const stateOf = (index) =>
    index < current ? "done" : index === current ? "active" : "pending";

  return (
    <div
      className="relative -mx-4 -mb-4 flex min-h-[calc(100vh-4rem)] justify-center overflow-hidden"
      style={{
        backgroundImage:
          "linear-gradient(146deg, #F0F4FF 0%, #F0FFF4 50%, #F8F0FF 100%)",
      }}
    >
      <style>{`
        @keyframes puBlobA { from { transform: translate(0,0) scale(1); } to { transform: translate(60px,40px) scale(1.15); } }
        @keyframes puBlobB { from { transform: translate(0,0) scale(1.1); } to { transform: translate(-50px,-60px) scale(0.95); } }
        @keyframes puBlobC { from { transform: translate(0,0) scale(0.95); } to { transform: translate(40px,50px) scale(1.12); } }
        @keyframes puCardIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes puPopIn { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }
        .pu-blob-a { animation: puBlobA 16s ease-in-out infinite alternate; }
        .pu-blob-b { animation: puBlobB 18s ease-in-out infinite alternate; }
        .pu-blob-c { animation: puBlobC 20s ease-in-out infinite alternate; }
        .pu-card-in { animation: puCardIn .5s ease-out both; }
        .pu-pop-in { animation: puPopIn .3s ease-out both; }
        @media (prefers-reduced-motion: reduce) {
          .pu-blob-a, .pu-blob-b, .pu-blob-c, .pu-card-in, .pu-pop-in { animation: none; }
        }
      `}</style>

      {/* Blob sfocati del mesh gradient (colori e misure dal Figma) */}
      <div
        className="pu-blob-a absolute left-[11%] top-[23%] size-[359px] rounded-full opacity-15 blur-[57px]"
        style={{ backgroundImage: "linear-gradient(135deg, #4285F4, #9C4EFF)" }}
      />
      <div
        className="pu-blob-b absolute left-[82%] top-[58%] size-[309px] rounded-full opacity-15 blur-[57px]"
        style={{ backgroundImage: "linear-gradient(135deg, #34A853, #0F9D58)" }}
      />
      <div
        className="pu-blob-c absolute left-[52%] top-[4%] size-[338px] rounded-full opacity-15 blur-[72px]"
        style={{ backgroundImage: "linear-gradient(135deg, #9C4EFF, #4285F4)" }}
      />

      <div className="pu-card-in relative mt-[14vh] h-fit w-[512px] max-w-[92vw] rounded-2xl bg-white p-8 shadow-[0_10px_7.5px_rgba(0,0,0,0.05),0_4px_3px_rgba(0,0,0,0.05)]">
        {steps.map((step, index) => {
          const state = stateOf(index);
          const Icon = step.icon;
          return (
            <div
              key={step.key}
              className={`flex items-center gap-4 border-b border-[#E2E8F0] py-4 transition-opacity duration-500 first:pt-0 last:border-b-0 last:pb-0 ${
                state === "pending" ? "opacity-50" : "opacity-100"
              }`}
            >
              <div
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-colors duration-300 ${
                  state === "done" ? "bg-[#0F172A]" : "bg-[#F3F4F6]"
                }`}
              >
                {state === "done" ? (
                  <Check className="pu-pop-in h-5 w-5 text-[#F8FAFC]" />
                ) : (
                  <Icon className="h-5 w-5 text-[#020617]" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-base font-medium leading-6 text-[#020617]">
                  {t(`onboardingWizard.${step.key}Title`)}
                </p>
                <p className="text-xs leading-4 tracking-[0.18px] text-[#64748B]">
                  {t(`onboardingWizard.${step.key}Sub`)}
                </p>
              </div>
              {state === "active" && (
                <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[#64748B]" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CreationLoader;
